import { FormTheme } from '../types';

const backgroundDeclaration = (value: string): string =>
  /gradient/i.test(value) ? `background: ${value} !important;` : `background-color: ${value} !important;`;


const cssUrl = (value: string): string => `url("${value.replace(/["\\]/g, '\\$&')}")`;


const backgroundDeclarationWithImage = (theme: FormTheme): string => {
  const fill = theme.background || '#ffffff';
  const imageUrl = theme.backgroundImageUrl?.trim();
  if (!imageUrl) return backgroundDeclaration(fill);

  const size = theme.backgroundImageSize || 'cover';
  const isGradientFill = /gradient/i.test(fill);
  const imageLayer = cssUrl(imageUrl);

  return `
    background-image: ${isGradientFill ? `${fill}, ${imageLayer}` : imageLayer} !important;
    background-color: ${isGradientFill ? 'transparent' : fill} !important;
    background-size: ${size}${isGradientFill ? ', cover' : ''} !important;
    background-position: center center !important;
    background-repeat: no-repeat !important;
  `;
};


export const backgroundSx = (theme: FormTheme | null | undefined) => {
  const fill = theme?.background || '#ffffff';
  const imageUrl = theme?.backgroundImageUrl?.trim();
  if (!imageUrl) return { background: fill };

  const size = theme?.backgroundImageSize || 'cover';
  const isGradientFill = /gradient/i.test(fill);
  return {
    backgroundImage: isGradientFill ? `${fill}, url("${imageUrl}")` : `url("${imageUrl}")`,
    backgroundColor: isGradientFill ? 'transparent' : fill,
    backgroundSize: isGradientFill ? `${size}, cover` : size,
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat',
  };
};

export const generateLayoutCSS = (selector: string = '', forceStackedColumns = false): string => {
  const prefix = selector ? `${selector} ` : '';

  const stackedRule = `
      ${prefix} .row:has(> [class*='col-md-']),
      ${prefix} .react-form-generator .row,
      ${prefix} .form-preview-modal .row {
        flex-direction: column !important;
      }

      ${prefix} [class*='col-md-'],
      ${prefix} .react-form-generator [class*='col-md-'],
      ${prefix} .form-preview-modal [class*='col-md-'] {
        flex: 1 1 100% !important;
        width: 100% !important;
      }
  `;

  return `
    ${prefix} .rfb-item .row,
    ${prefix} .react-form-generator .rfb-item .row,
    ${prefix} .form-preview-modal .rfb-item .row {
      border: none !important;
      border-radius: 0 !important;
      margin-top: 0 !important;
      padding: 0 !important;
    }

    ${prefix} .row:has(> [class*='col-md-']),
    ${prefix} .react-form-generator .row,
    ${prefix} .form-preview-modal .row {
      display: flex !important;
      flex-wrap: wrap !important;
      gap: 16px !important;
      margin: 0 !important;
    }

    ${prefix} [class*='col-md-'],
    ${prefix} .react-form-generator [class*='col-md-'],
    ${prefix} .form-preview-modal [class*='col-md-'] {
      flex: 1 1 0 !important;
      max-width: none !important;
      min-width: 0 !important;
      padding: 0 !important;
    }

    @media (max-width: 768px) {
      ${stackedRule}
    }
    ${forceStackedColumns ? stackedRule : ''}

    ${prefix} .rfb-item,
    ${prefix} .SortableItem,
    ${prefix}.react-form-generator .rfb-item,
    ${prefix}.react-form-builder-preview .rfb-item,
    ${prefix} .react-form-generator .rfb-item,
    ${prefix} .react-form-builder-preview .rfb-item {
      border: none !important;
      box-shadow: none !important;
      background: transparent !important;
    }
  `;
};

export const generateThemeCSS = (
  themeOrNull: FormTheme | null,
  selector: string = '',
  skipBackground = false,
): string => {

  const theme = themeOrNull || {};

  const prefix = selector ? `${selector} ` : '';


  return `
    ${prefix}.react-form-generator,
    ${prefix}.react-form-builder-preview,
    ${prefix} .react-form-generator,
    ${prefix} .react-form-builder-preview,
    ${prefix} form {
      font-family: ${theme.paragraphFontFamily || theme.fontFamily || 'Arial, sans-serif'} !important;
      color: ${theme.paragraph || '#333333'} !important;
      ${skipBackground ? '' : backgroundDeclarationWithImage(theme)}
    }

    ${prefix} h1,
    ${prefix} h2,
    ${prefix} h3,
    ${prefix} h4,
    ${prefix} h5,
    ${prefix} h6,
    ${prefix}.react-form-generator h1,
    ${prefix}.react-form-generator h2,
    ${prefix}.react-form-generator h3,
    ${prefix}.react-form-generator h4,
    ${prefix}.react-form-generator h5,
    ${prefix}.react-form-generator h6,
    ${prefix}.react-form-builder-preview h1,
    ${prefix}.react-form-builder-preview h2,
    ${prefix}.react-form-builder-preview h3,
    ${prefix}.react-form-builder-preview h4,
    ${prefix}.react-form-builder-preview h5,
    ${prefix}.react-form-builder-preview h6,
    ${prefix} .react-form-generator h1,
    ${prefix} .react-form-generator h2,
    ${prefix} .react-form-generator h3,
    ${prefix} .react-form-generator h4,
    ${prefix} .react-form-generator h5,
    ${prefix} .react-form-generator h6,
    ${prefix} .react-form-builder-preview h1,
    ${prefix} .react-form-builder-preview h2,
    ${prefix} .react-form-builder-preview h3,
    ${prefix} .react-form-builder-preview h4,
    ${prefix} .react-form-builder-preview h5,
    ${prefix} .react-form-builder-preview h6,
    ${prefix} form h1,
    ${prefix} form h2,
    ${prefix} form h3,
    ${prefix} form h4,
    ${prefix} form h5,
    ${prefix} form h6 {
      color: ${theme.heading1 || '#333333'} !important;
      font-family: ${theme.fontFamily || 'Arial, sans-serif'} !important;
      font-weight: ${theme.fontWeight || '700'} !important;
    }

    /* First, set all labels to paragraph color (including option labels) */
    ${prefix} .form-group label,
    ${prefix} .checkbox label,
    ${prefix} .radio label,
    ${prefix} .form-check label,
    ${prefix} input[type="radio"] + label,
    ${prefix} input[type="checkbox"] + label,
    ${prefix}.react-form-generator .form-group label,
    ${prefix}.react-form-generator .checkbox label,
    ${prefix}.react-form-generator .radio label,
    ${prefix}.react-form-builder-preview .form-group label,
    ${prefix}.react-form-builder-preview .checkbox label,
    ${prefix}.react-form-builder-preview .radio label,
    ${prefix} .react-form-generator .form-group label,
    ${prefix} .react-form-generator .checkbox label,
    ${prefix} .react-form-generator .radio label,
    ${prefix} .react-form-builder-preview .form-group label,
    ${prefix} .react-form-builder-preview .checkbox label,
    ${prefix} .react-form-builder-preview .radio label,
    ${prefix} .react-form-generator input[type="radio"] + label,
    ${prefix} .react-form-generator input[type="checkbox"] + label,
    ${prefix} .react-form-builder-preview input[type="radio"] + label,
    ${prefix} .react-form-builder-preview input[type="checkbox"] + label,
    ${prefix} form .form-group label,
    ${prefix} form .checkbox label,
    ${prefix} form .radio label,
    ${prefix} form input[type="radio"] + label,
    ${prefix} form input[type="checkbox"] + label,
    ${prefix} .rfb-item .form-group label,
    ${prefix} .rfb-item .checkbox label,
    ${prefix} .rfb-item .radio label,
    ${prefix} .rfb-item input[type="radio"] + label,
    ${prefix} .rfb-item input[type="checkbox"] + label {
      color: ${theme.paragraph || '#333333'} !important;
    }

    ${prefix} .form-group > label,
    ${prefix}.react-form-generator .form-group > label,
    ${prefix}.react-form-builder-preview .form-group > label,
    ${prefix}.react-form-generator .rfb-item .form-group > label,
    ${prefix}.react-form-builder-preview .rfb-item .form-group > label,
    ${prefix} .react-form-generator .form-group > label,
    ${prefix} .react-form-builder-preview .form-group > label,
    ${prefix} .react-form-generator .rfb-item .form-group > label,
    ${prefix} .react-form-builder-preview .rfb-item .form-group > label,
    ${prefix} form .form-group > label,
    ${prefix} .address-caption {
      color: ${theme.label || '#333333'} !important;
      font-family: ${theme.paragraphFontFamily || theme.fontFamily || 'Arial, sans-serif'} !important;
    }

    ${prefix} .address-caption {
   color: ${theme.label || '#333333'} !important;
      font-family: ${theme.paragraphFontFamily || theme.fontFamily || 'Arial, sans-serif'} !important;
    }

    ${prefix} .form-group label,
    ${prefix} .form-group label *,
    ${prefix} .rfb-item label,
    ${prefix} .rfb-item label *,
    ${prefix} form label,
    ${prefix} form label * {
      background: transparent !important;
      background-color: transparent !important;
    }

    ${prefix} .form-group > label *:not(.label-danger):not(.label-required):not(.badge),
    ${prefix} .rfb-item .form-group > label *:not(.label-danger):not(.label-required):not(.badge),
    ${prefix} form .form-group > label *:not(.label-danger):not(.label-required):not(.badge) {
      color: ${theme.label || '#333333'} !important;
    }

    ${prefix} .checkbox label *:not(.label-danger):not(.label-required):not(.badge),
    ${prefix} .radio label *:not(.label-danger):not(.label-required):not(.badge),
    ${prefix} .form-check label *:not(.label-danger):not(.label-required):not(.badge) {
      color: ${theme.paragraph || '#333333'} !important;
    }

    ${prefix} a,
    ${prefix}.react-form-generator a,
    ${prefix}.react-form-builder-preview a,
    ${prefix} .react-form-generator a,
    ${prefix} .react-form-builder-preview a,
    ${prefix} form a {
      color: ${theme.link || '#1677ff'} !important;
    }

    ${prefix} input[type="text"],
    ${prefix} input[type="email"],
    ${prefix} input[type="number"],
    ${prefix} input[type="tel"],
    ${prefix} input[type="url"],
    ${prefix} input[type="password"],
    ${prefix} input[type="date"],
    ${prefix} input[type="time"],
    ${prefix} input[type="datetime-local"],
    ${prefix} input[type="month"],
    ${prefix} select,
    ${prefix} .PhoneInput,
    ${prefix}.react-form-generator input[type="text"],
    ${prefix}.react-form-generator input[type="email"],
    ${prefix}.react-form-generator input[type="number"],
    ${prefix}.react-form-generator input[type="tel"],
    ${prefix}.react-form-generator input[type="url"],
    ${prefix}.react-form-generator input[type="password"],
    ${prefix}.react-form-generator input[type="date"],
    ${prefix}.react-form-generator input[type="time"],
    ${prefix}.react-form-generator input[type="datetime-local"],
    ${prefix}.react-form-generator input[type="month"],
    ${prefix}.react-form-generator select,
    ${prefix}.react-form-generator .PhoneInput,
    ${prefix}.react-form-builder-preview input[type="text"],
    ${prefix}.react-form-builder-preview input[type="email"],
    ${prefix}.react-form-builder-preview input[type="number"],
    ${prefix}.react-form-builder-preview input[type="tel"],
    ${prefix}.react-form-builder-preview input[type="url"],
    ${prefix}.react-form-builder-preview input[type="password"],
    ${prefix}.react-form-builder-preview input[type="date"],
    ${prefix}.react-form-builder-preview input[type="time"],
    ${prefix}.react-form-builder-preview input[type="datetime-local"],
    ${prefix}.react-form-builder-preview input[type="month"],
    ${prefix}.react-form-builder-preview select,
    ${prefix}.react-form-builder-preview .PhoneInput,
    ${prefix} .react-form-generator input[type="text"],
    ${prefix} .react-form-generator input[type="email"],
    ${prefix} .react-form-generator input[type="number"],
    ${prefix} .react-form-generator input[type="tel"],
    ${prefix} .react-form-generator input[type="url"],
    ${prefix} .react-form-generator input[type="password"],
    ${prefix} .react-form-generator input[type="date"],
    ${prefix} .react-form-generator input[type="time"],
    ${prefix} .react-form-generator input[type="datetime-local"],
    ${prefix} .react-form-generator input[type="month"],
    ${prefix} .react-form-generator select,
    ${prefix} .react-form-generator .PhoneInput,
    ${prefix} .react-form-builder-preview input[type="text"],
    ${prefix} .react-form-builder-preview input[type="email"],
    ${prefix} .react-form-builder-preview input[type="number"],
    ${prefix} .react-form-builder-preview input[type="tel"],
    ${prefix} .react-form-builder-preview input[type="url"],
    ${prefix} .react-form-builder-preview input[type="password"],
    ${prefix} .react-form-builder-preview input[type="date"],
    ${prefix} .react-form-builder-preview input[type="time"],
    ${prefix} .react-form-builder-preview input[type="datetime-local"],
    ${prefix} .react-form-builder-preview input[type="month"],
    ${prefix} .react-form-builder-preview select,
    ${prefix} .react-form-builder-preview .PhoneInput,
    ${prefix} form input[type="text"],
    ${prefix} form input[type="email"],
    ${prefix} form input[type="number"],
    ${prefix} form input[type="tel"],
    ${prefix} form input[type="url"],
    ${prefix} form input[type="password"],
    ${prefix} form input[type="date"],
    ${prefix} form input[type="time"],
    ${prefix} form input[type="datetime-local"],
    ${prefix} form input[type="month"],
    ${prefix} form select,
    ${prefix} form .PhoneInput {
      color: ${theme.formInputText || '#808080'} !important;
      background-color: ${theme.formInputBackground || '#ffffff'} !important;
      border: 1px solid ${theme.formInputBorder || '#d1d5db'} !important;
      border-radius: ${theme.borderRadius ?? 8}px !important;
      height: ${theme.formInputHeight ?? 42}px !important;
      font-size: ${theme.formInputFontSize ?? 14}px !important;
      padding: 0 12px !important;
      box-shadow: none !important;
      transition: border-color 0.15s ease, box-shadow 0.15s ease !important;
    }


    ${prefix} .multi-select-field,
    ${prefix}.react-form-generator .multi-select-field,
    ${prefix}.react-form-builder-preview .multi-select-field,
    ${prefix} .react-form-generator .multi-select-field,
    ${prefix} .react-form-builder-preview .multi-select-field,
    ${prefix} form .multi-select-field {
      color: ${theme.formInputText || '#808080'} !important;
      background-color: ${theme.formInputBackground || '#ffffff'} !important;
      border: 1px solid ${theme.formInputBorder || '#d1d5db'} !important;
      border-radius: ${theme.borderRadius ?? 8}px !important;
      min-height: ${theme.formInputHeight ?? 42}px !important;
      font-size: ${theme.formInputFontSize ?? 14}px !important;
      box-shadow: none !important;
    }

    ${prefix} .multi-select-placeholder,
    ${prefix} form .multi-select-placeholder {
      color: ${theme.formInputPlaceholder || '#9ca3af'} !important;
    }

    ${prefix} input::placeholder,
    ${prefix} textarea::placeholder,
    ${prefix} .PhoneInput input::placeholder,
    ${prefix}.react-form-generator input::placeholder,
    ${prefix}.react-form-generator textarea::placeholder,
    ${prefix}.react-form-builder-preview input::placeholder,
    ${prefix}.react-form-builder-preview textarea::placeholder,
    ${prefix} .react-form-generator input::placeholder,
    ${prefix} .react-form-generator textarea::placeholder,
    ${prefix} .react-form-builder-preview input::placeholder,
    ${prefix} .react-form-builder-preview textarea::placeholder,
    ${prefix} form input::placeholder,
    ${prefix} form textarea::placeholder {
      color: ${theme.formInputPlaceholder || '#9ca3af'} !important;
      opacity: 1 !important;
    }

 
    ${prefix} .ahd-icon-input-wrap {
      position: relative !important;
      display: block !important;
      width: 100% !important;
    }

    ${prefix} .ahd-icon-input-wrap > .ahd-icon-input-icon {
      position: absolute !important;
      left: 12px !important;
      top: 50% !important;
      transform: translateY(-50%) !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      line-height: 0 !important;
      pointer-events: none !important;
      z-index: 1 !important;
    }

 
    ${prefix} .address-caption {
      opacity: 0.75 !important;
    }

 
    ${prefix} .ahd-icon-input-wrap > .ahd-icon-input-icon.datetime-input-icon {
      pointer-events: auto !important;
      cursor: pointer !important;
      z-index: 2 !important;
    }

    ${prefix} .ahd-icon-input-wrap > input.with-icon,
    ${prefix}.react-form-generator .ahd-icon-input-wrap > input.with-icon,
    ${prefix}.react-form-builder-preview .ahd-icon-input-wrap > input.with-icon,
    ${prefix} .react-form-generator .ahd-icon-input-wrap > input.with-icon,
    ${prefix} .react-form-builder-preview .ahd-icon-input-wrap > input.with-icon,
    ${prefix} form .ahd-icon-input-wrap > input.with-icon,
    ${prefix} .ahd-icon-input-wrap > select.with-icon,
    ${prefix}.react-form-generator .ahd-icon-input-wrap > select.with-icon,
    ${prefix}.react-form-builder-preview .ahd-icon-input-wrap > select.with-icon,
    ${prefix} .react-form-generator .ahd-icon-input-wrap > select.with-icon,
    ${prefix} .react-form-builder-preview .ahd-icon-input-wrap > select.with-icon,
    ${prefix} form .ahd-icon-input-wrap > select.with-icon {
      padding: 0 12px 0 38px !important;
      line-height: ${theme.formInputHeight ?? 42}px !important;
    }

    ${prefix} textarea,
    ${prefix}.react-form-generator textarea,
    ${prefix}.react-form-builder-preview textarea,
    ${prefix} .react-form-generator textarea,
    ${prefix} .react-form-builder-preview textarea,
    ${prefix} form textarea {
      /* No height property here, on purpose: the generic input rule above no
         longer matches textarea (it used to, and its own height !important
         blocked resizing — a fixed-height !important rule always wins over
         the plain inline style the browser writes when you drag-resize).
         With nothing else claiming height, min/max-height plus resize is all
         that's needed, and manual resizing is free to work. */
      min-height: ${theme.formTextareaHeight ?? 96}px !important;
      max-height: 640px !important;
      padding: 10px 12px !important;
      resize: vertical !important;
      overflow: auto !important;
    }

    ${prefix} input:focus,
    ${prefix} textarea:focus,
    ${prefix} select:focus,
    ${prefix}.react-form-generator input:focus,
    ${prefix}.react-form-generator textarea:focus,
    ${prefix}.react-form-generator select:focus,
    ${prefix}.react-form-builder-preview input:focus,
    ${prefix}.react-form-builder-preview textarea:focus,
    ${prefix}.react-form-builder-preview select:focus,
    ${prefix} .react-form-generator input:focus,
    ${prefix} .react-form-generator textarea:focus,
    ${prefix} .react-form-generator select:focus,
    ${prefix} .react-form-builder-preview input:focus,
    ${prefix} .react-form-builder-preview textarea:focus,
    ${prefix} .react-form-builder-preview select:focus,
    ${prefix} form input:focus,
    ${prefix} form textarea:focus,
    ${prefix} form select:focus {
      border-color: ${theme.link || '#1677ff'} !important;
      outline: none !important;
      box-shadow: 0 0 0 3px ${theme.link || '#1677ff'}33 !important;
    }

    ${prefix} button[type="submit"],
    ${prefix} .btn-primary,
    ${prefix}.react-form-generator button[type="submit"],
    ${prefix}.react-form-generator .btn-primary,
    ${prefix}.react-form-builder-preview button[type="submit"],
    ${prefix}.react-form-builder-preview .btn-primary,
    ${prefix} .react-form-generator button[type="submit"],
    ${prefix} .react-form-generator .btn-primary,
    ${prefix} .react-form-builder-preview button[type="submit"],
    ${prefix} .react-form-builder-preview .btn-primary,
    ${prefix} form button[type="submit"],
    ${prefix} form .btn-primary {
      ${backgroundDeclaration(theme.actionButtonBackground || '#1677ff')}
      color: ${theme.actionButtonText || '#ffffff'} !important;
      border: 1px solid ${theme.actionButtonBorder || theme.actionButtonBackground || '#1677ff'} !important;
      border-radius: ${theme.borderRadius ?? 8}px !important;
      padding: ${theme.buttonPaddingY ?? 10}px ${theme.buttonPaddingX ?? 20}px !important;
      font-family: ${theme.buttonFontFamily || theme.fontFamily || 'Arial, sans-serif'} !important;
      font-size: ${theme.buttonFontSize ?? 16}px !important;
      font-weight: ${theme.buttonFontWeight === 'regular' ? 400 : theme.buttonFontWeight || 700} !important;
      cursor: pointer !important;
      transition: background-color 0.3s ease, opacity 0.2s ease !important;
    }

    ${prefix} button[type="submit"]:hover,
    ${prefix} .btn-primary:hover,
    ${prefix}.react-form-generator button[type="submit"]:hover,
    ${prefix}.react-form-generator .btn-primary:hover,
    ${prefix}.react-form-builder-preview button[type="submit"]:hover,
    ${prefix}.react-form-builder-preview .btn-primary:hover,
    ${prefix} .react-form-generator button[type="submit"]:hover,
    ${prefix} .react-form-generator .btn-primary:hover,
    ${prefix} .react-form-builder-preview button[type="submit"]:hover,
    ${prefix} .react-form-builder-preview .btn-primary:hover,
    ${prefix} form button[type="submit"]:hover,
    ${prefix} form .btn-primary:hover {
      opacity: 0.9 !important;
    }

    ${prefix} button:not([type="submit"]):not(.btn-primary):not(.MuiButtonBase-root):not(.chip-select-chip):not(.time-slot-button):not(.scale-option),
    ${prefix}.react-form-generator button:not([type="submit"]):not(.btn-primary):not(.MuiButtonBase-root):not(.chip-select-chip):not(.time-slot-button):not(.scale-option),
    ${prefix}.react-form-builder-preview button:not([type="submit"]):not(.btn-primary):not(.MuiButtonBase-root):not(.chip-select-chip):not(.time-slot-button):not(.scale-option),
    ${prefix} .react-form-generator button:not([type="submit"]):not(.btn-primary):not(.MuiButtonBase-root):not(.chip-select-chip):not(.time-slot-button):not(.scale-option),
    ${prefix} .react-form-builder-preview button:not([type="submit"]):not(.btn-primary):not(.MuiButtonBase-root):not(.chip-select-chip):not(.time-slot-button):not(.scale-option),
    ${prefix} form button:not([type="submit"]):not(.btn-primary):not(.MuiButtonBase-root):not(.chip-select-chip):not(.time-slot-button):not(.scale-option) {
      ${backgroundDeclaration(theme.basicButtonBackground || '#add8e6')}
      color: ${theme.basicButtonText || '#1677ff'} !important;
      border: 1px solid ${theme.basicButtonBorder || theme.basicButtonText || '#1677ff'} !important;
      border-radius: ${theme.borderRadius ?? 8}px !important;
      padding: ${theme.buttonPaddingY ?? 10}px ${theme.buttonPaddingX ?? 20}px !important;
      font-family: ${theme.buttonFontFamily || theme.fontFamily || 'Arial, sans-serif'} !important;
      font-size: ${theme.buttonFontSize ?? 16}px !important;
      font-weight: ${theme.buttonFontWeight === 'regular' ? 400 : theme.buttonFontWeight || 700} !important;
      cursor: pointer !important;
      transition: background-color 0.3s ease, opacity 0.2s ease !important;
    }

 
    ${prefix} .chip-select-chip.chip-select-chip,
    ${prefix}.react-form-generator .chip-select-chip.chip-select-chip,
    ${prefix}.react-form-builder-preview .chip-select-chip.chip-select-chip,
    ${prefix} .react-form-generator .chip-select-chip.chip-select-chip,
    ${prefix} .react-form-builder-preview .chip-select-chip.chip-select-chip,
    ${prefix} form .chip-select-chip.chip-select-chip {
      padding: ${theme.chipPaddingY ?? 6}px ${theme.chipPaddingX ?? 10}px !important;
      border-radius: ${theme.chipRadius ?? theme.borderRadius ?? 8}px !important;
      font-family: ${theme.fontFamily || 'Arial, sans-serif'} !important;
      font-size: ${theme.chipFontSize ?? 14}px !important;
      font-weight: ${theme.chipFontWeight === 'regular' ? 400 : theme.chipFontWeight || 400} !important;
      background: ${theme.chipBackground || '#ffffff'} !important;
      color: ${theme.chipText || '#374151'} !important;
      border: 1.5px solid ${theme.chipBorder || '#e5e7eb'} !important;
    }

    ${prefix} .chip-select-chip.chip-select-chip:hover:not(:disabled):not(.is-selected),
    ${prefix}.react-form-generator .chip-select-chip.chip-select-chip:hover:not(:disabled):not(.is-selected),
    ${prefix}.react-form-builder-preview .chip-select-chip.chip-select-chip:hover:not(:disabled):not(.is-selected),
    ${prefix} .react-form-generator .chip-select-chip.chip-select-chip:hover:not(:disabled):not(.is-selected),
    ${prefix} .react-form-builder-preview .chip-select-chip.chip-select-chip:hover:not(:disabled):not(.is-selected),
    ${prefix} form .chip-select-chip.chip-select-chip:hover:not(:disabled):not(.is-selected) {
      background: ${theme.chipHoverBackground || '#ffffff'} !important;
      border-color: ${theme.chipHoverBorder || '#c4b5fd'} !important;
    }
 
    ${prefix} .chip-select-chip.chip-select-chip.is-selected,
    ${prefix} .chip-select-chip.chip-select-chip.is-selected:hover,
    ${prefix}.react-form-generator .chip-select-chip.chip-select-chip.is-selected,
    ${prefix}.react-form-builder-preview .chip-select-chip.chip-select-chip.is-selected,
    ${prefix} .react-form-generator .chip-select-chip.chip-select-chip.is-selected,
    ${prefix} .react-form-builder-preview .chip-select-chip.chip-select-chip.is-selected,
    ${prefix} form .chip-select-chip.chip-select-chip.is-selected {
      background: ${theme.chipSelectedBackground || '#ffffff'} !important;
      color: ${theme.chipSelectedText || '#7c3aed'} !important;
      border-color: ${theme.chipSelectedBorder || '#8b5cf6'} !important;
      --chip-indicator: ${theme.chipSelectedBorder || theme.chipSelectedText || '#8b5cf6'};
    }

    ${prefix} .datepicker-wrapper {
      --slot-accent: ${theme.basicButtonText || '#1677ff'};
    }

    ${prefix} .quiz-block {
      --quiz-accent: ${theme.actionButtonBackground || theme.stepperActiveColor || '#4f46e5'};
    }

    ${prefix} .scale-block {
      --scale-accent: ${theme.actionButtonBackground || theme.stepperActiveColor || '#4f46e5'};
    }

    ${prefix}.react-form-generator .form-group,
    ${prefix}.react-form-builder-preview .form-group,
    ${prefix} .react-form-generator .form-group,
    ${prefix} .react-form-builder-preview .form-group {
      margin-bottom: 15px !important;
    }

    ${prefix} .edit-form input,
    ${prefix} .edit-form textarea,
    ${prefix} .edit-form select {
      color: inherit !important;
      background-color: #ffffff !important;
      border: 1px solid #d1d5db !important;
      border-radius: 4px !important;
      padding: 6px 8px !important;
      height: auto !important;
      min-height: 0 !important;
      box-shadow: none !important;
    }

    ${prefix} .edit-form input:focus,
    ${prefix} .edit-form textarea:focus,
    ${prefix} .edit-form select:focus {
      border-color: #9ca3af !important;
      box-shadow: none !important;
    }

    ${prefix} .edit-form button,
    ${prefix} .edit-form .btn,
    ${prefix} .edit-form .btn-primary,
    ${prefix} .edit-form button[type="submit"] {
      background-color: initial !important;
      color: inherit !important;
      border-radius: 4px !important;
      padding: initial !important;
    }
  `;
};
